import { useTranslation } from 'react-i18next';

import { NgButton, PositionSourceBadgeWithInfo } from '@/ui';
import type { Position, PositionSource } from '@/signalr';
import { toUiPositionSource } from '../../model/positionSource';

export type CallerCurrentPositionMarkerDetailTemplateProps = {
  currentPosition: Position;
  positions: readonly Position[];
  onSelect: (positionSource: PositionSource) => void | Promise<void>;
  openManualPositionDialog: () => void | Promise<void>;
  allowSourceSwitching?: boolean;
};

export function CallerCurrentPositionMarkerDetailTemplate({
  currentPosition,
  positions,
  onSelect,
  openManualPositionDialog,
  allowSourceSwitching = false,
}: CallerCurrentPositionMarkerDetailTemplateProps) {
  const { t } = useTranslation();

  const sortedPositions = positions
    .filter((position) => position.positionSource !== currentPosition.positionSource)
    .sort((a, b) => Date.parse(b.captureAtUtc) - Date.parse(a.captureAtUtc));

  return (
    <div className="d-flex flex-column align-items-start gap-2">
      <h5>{t('Incident_CurrentPositionMarker_Header')}</h5>
      <div>
        <h6>{t('Incident_CurrentPositionMarker_CurrentSource')}</h6>
        <PositionSourceBadgeWithInfo
          positionSource={toUiPositionSource(currentPosition.positionSource)}
          timestamp={new Date(currentPosition.captureAtUtc)}
          accuracy={currentPosition.accuracy}
        />
      </div>
      <div className="d-flex flex-column w-100">
        {sortedPositions.length > 0 && (
          <>
            <h6>{t('Incident_CurrentPositionMarker_AvailableSources')}</h6>
            {sortedPositions.map((item) => (
              <div
                key={item.positionSource}
                className="d-flex flex-row justify-content-between align-items-center gap-2 py-1 border-bottom border-light"
              >
                <PositionSourceBadgeWithInfo
                  positionSource={toUiPositionSource(item.positionSource)}
                  timestamp={new Date(item.captureAtUtc)}
                  accuracy={item.accuracy}
                />
                {allowSourceSwitching && (
                  <NgButton size="small" onClick={() => void onSelect(item.positionSource)}>
                    {t('Incident_CurrentPositionMarker_UseButton')}
                  </NgButton>
                )}
              </div>
            ))}
          </>
        )}
        {allowSourceSwitching && (
          <div className="d-flex flex-row justify-content-center align-items-center gap-2 pt-2">
            <NgButton
              text={
                positions.length > 0
                  ? t('Incident_OpenEditGpsLocationDialog_ChangePosition')
                  : t('Incident_OpenEditGpsLocationDialog_NoPosition')
              }
              icon="fal fa-pen-to-square"
              size="small"
              onClick={() => void openManualPositionDialog()}
            ></NgButton>
          </div>
        )}
      </div>
    </div>
  );
}
